// src/ui/Table.ts
//
// Landscape board layout (Phase 21 redesign; row-swap in Phase 22):
//   Row 0: dozen subcells (dozen-sub; each dozen D1/D2/D3 split into 3 equal visual subcells)
//   Rows 1-3: number cells (cols 1-12) plus zero (col 0, spans rows 1-3) and column bets (col 13)
//   Row 4: 12 outside-sub cells (2 per parent × 6 even-money bets, each 1 column wide)
//
// Gesture detection (setOnBetPlace):
//   Horizontal drag → split/corner across columns (n±3)
//   Vertical drag   → split/corner across rows (n±1)

import Phaser from 'phaser';
import { RED_NUMBERS } from '../systems/wheel';
import type { BetType, BoardCell, BoardLayout, OutsideBetKind, SlotNumber } from '../state/types';
import { addCrispText } from './textHelper';
// Phase 21 (WR-02): cellLabelForSub extracted to betLabel.ts so CellPickerModal
// can share the same logic without importing Phaser.
import { cellLabelForSub } from './betLabel';

interface TableCell {
  bet: BetType;
  rect: Phaser.GameObjects.Rectangle;
}

export class Table extends Phaser.GameObjects.Container {
  private cells: TableCell[] = [];
  private pointerupListener: ((ptr: Phaser.Input.Pointer) => void) | null = null;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    private readonly layout: BoardLayout,
    private readonly opts: { boardWidth?: number; boardHeight?: number } = {},
  ) {
    super(scene, x, y);
    scene.add.existing(this);
    this.draw();
  }

  private draw(): void {
    const boardWidth = this.opts.boardWidth ?? 375;
    const boardHeight = this.opts.boardHeight ?? 130;
    const colW = boardWidth / 14;
    const rowH = boardHeight / 5;

    // Pre-compute subcell render groups before the main loop.
    const outsideGroups = buildOutsideSubGroups(this.layout.cells);
    const dozenClusters = buildDozenSubClusters(this.layout.cells);

    // Build a skip-set so the main loop ignores all subcells.
    const skipCells = new Set<BoardCell>(
      this.layout.cells.filter((c) => c.bet.kind === 'outside-sub' || c.bet.kind === 'dozen-sub'),
    );

    // Main loop: regular cells only (number, zero, column bets).
    for (const cell of this.layout.cells) {
      if (skipCells.has(cell)) continue;

      const cellX = cell.col * colW;
      const cellY = cell.row * rowH;
      let cellW: number;
      let cellH: number;

      if (cell.col === 0) {
        // Zero: visually spans all 3 number rows (rows 1-3).
        cellW = colW;
        cellH = rowH * 3;
      } else {
        cellW = colW;
        cellH = rowH;
      }

      const fillColor = cell.bleached ? 0xffffff : this.cellFillColor(cell.bet);
      const labelStr = this.cellLabel(cell.bet);

      const rect = this.scene.add
        .rectangle(cellX, cellY, cellW, cellH, fillColor)
        .setOrigin(0, 0)
        .setStrokeStyle(0.5, 0xffffff);

      const fontSize = cell.col === 0 ? '10px' : cell.row >= 3 ? '8px' : '9px';
      const label = addCrispText(this.scene, cellX + cellW / 2, cellY + cellH / 2, labelStr, {
        color: cell.bleached ? '#000000' : '#ffffff',
        fontSize,
      }).setOrigin(0.5);

      this.add([rect, label]);
      this.cells.push({ bet: cell.bet, rect });
    }

    // Render outside-sub groups.
    for (const group of outsideGroups) {
      this.renderOutsideSubGroup(group, colW, rowH);
    }

    // Render dozen-sub clusters (replaces the old fixed-position outer-border post-pass).
    for (const cluster of dozenClusters) {
      this.renderDozenSubCluster(cluster, colW, rowH);
    }
  }

  private renderOutsideSubGroup(
    group: { cells: BoardCell[]; combined: boolean },
    colW: number,
    rowH: number,
  ): void {
    if (group.combined && group.cells.length === 2) {
      const [left, right] = group.cells as [BoardCell, BoardCell];
      const fillColor = this.cellFillColor(left.bet);
      const parentKind = (left.bet as Extract<BetType, { kind: 'outside-sub' }>).parentKind;
      const labelStr = outsideSubParentLabel(parentKind);
      const x = left.col * colW;
      const y = left.row * rowH;
      // 2-col visual background (decoration only).
      const visualRect = this.scene.add
        .rectangle(x, y, colW * 2, rowH, fillColor)
        .setOrigin(0, 0)
        .setStrokeStyle(0.5, 0xffffff);
      const label = addCrispText(this.scene, x + colW, y + rowH / 2, labelStr, {
        color: '#ffffff',
        fontSize: '9px',
      }).setOrigin(0.5);
      this.add([visualRect, label]);
      // Transparent 1-col hit-test rect per subcell.
      for (const cell of [left, right]) {
        const rect = this.scene.add
          .rectangle(cell.col * colW, cell.row * rowH, colW, rowH, 0x000000)
          .setOrigin(0, 0)
          .setAlpha(0);
        this.add([rect]);
        this.cells.push({ bet: cell.bet, rect });
      }
    } else {
      // Standalone: 1-col visible cell with stroke and parent label.
      const cell = group.cells[0]!;
      const fillColor = this.cellFillColor(cell.bet);
      const parentKind = (cell.bet as Extract<BetType, { kind: 'outside-sub' }>).parentKind;
      const labelStr = outsideSubParentLabel(parentKind);
      const x = cell.col * colW;
      const y = cell.row * rowH;
      const rect = this.scene.add
        .rectangle(x, y, colW, rowH, fillColor)
        .setOrigin(0, 0)
        .setStrokeStyle(0.5, 0xffffff);
      const label = addCrispText(this.scene, x + colW / 2, y + rowH / 2, labelStr, {
        color: '#ffffff',
        fontSize: '9px',
      }).setOrigin(0.5);
      this.add([rect, label]);
      this.cells.push({ bet: cell.bet, rect });
    }
  }

  private renderDozenSubCluster(
    cluster: { parentWhich: 1 | 2 | 3; cells: BoardCell[] },
    colW: number,
    rowH: number,
  ): void {
    const { parentWhich, cells } = cluster;
    const labelStr = `D${parentWhich}`;
    const y = cells[0]!.row * rowH;

    if (cells.length > 1) {
      // Combined: fill-only rects (each exactly 1 colW) + outer border + centered label.
      // With 4 subcells per dozen each occupying exactly 1 grid column, no width
      // scaling is needed — the natural colW positions give the correct 4-col span.
      const minCol = Math.min(...cells.map((c) => c.col));
      const maxCol = Math.max(...cells.map((c) => c.col));
      const spanX = minCol * colW;
      const spanW = (maxCol - minCol + 1) * colW;
      for (const cell of cells) {
        const fillX = cell.col * colW;
        const fillRect = this.scene.add.rectangle(fillX, y, colW, rowH, 0x064020).setOrigin(0, 0);
        this.add([fillRect]);
        // Hit-test rect co-located with visual fill.
        const rect = this.scene.add
          .rectangle(fillX, y, colW, rowH, 0x000000)
          .setOrigin(0, 0)
          .setAlpha(0);
        this.add([rect]);
        this.cells.push({ bet: cell.bet, rect });
      }
      const border = this.scene.add
        .rectangle(spanX, y, spanW, rowH, 0x000000, 0)
        .setOrigin(0, 0)
        .setStrokeStyle(0.5, 0xffffff);
      const label = addCrispText(this.scene, spanX + spanW / 2, y + rowH / 2, labelStr, {
        color: '#ffffff',
        fontSize: '9px',
      }).setOrigin(0.5);
      this.add([border, label]);
    } else {
      // Standalone: normal bordered cell with label.
      const cell = cells[0]!;
      const x = cell.col * colW;
      const rect = this.scene.add
        .rectangle(x, y, colW, rowH, 0x064020)
        .setOrigin(0, 0)
        .setStrokeStyle(0.5, 0xffffff);
      const label = addCrispText(this.scene, x + colW / 2, y + rowH / 2, labelStr, {
        color: '#ffffff',
        fontSize: '9px',
      }).setOrigin(0.5);
      this.add([rect, label]);
      this.cells.push({ bet: cell.bet, rect });
    }
  }

  private cellFillColor(bet: BetType): number {
    if (bet.kind === 'straight') {
      if (bet.number === 0) return 0x2a8a3a;
      return isRed(bet.number) ? 0xb00020 : 0x111111;
    }
    if (bet.kind === 'red') return 0xb00020;
    if (bet.kind === 'black') return 0x111111;
    // Phase 21 (BOARD-21): outside-sub cell color derived from parentKind
    if (bet.kind === 'outside-sub') {
      if (bet.parentKind === 'red') return 0xb00020;
      if (bet.parentKind === 'black') return 0x111111;
      return 0x064020;
    }
    return 0x064020;
  }

  private cellLabel(bet: BetType): string {
    if (bet.kind === 'straight') return String(bet.number);
    if (bet.kind === 'red') return '♦';
    if (bet.kind === 'black') return '♠';
    if (bet.kind === 'even') return 'EVEN';
    if (bet.kind === 'odd') return 'ODD';
    if (bet.kind === 'low') return '1-18';
    if (bet.kind === 'high') return '19-36';
    if (bet.kind === 'dozen') return `D${bet.which}`;
    if (bet.kind === 'column') return `C${bet.which}`;
    // Phase 21 (BOARD-21): outside-sub compact label
    if (bet.kind === 'outside-sub') return cellLabelForSub(bet);
    return '';
  }

  /** Return world-space bounds for the cell matching `bet`, or null. */
  public getCellBounds(bet: BetType): Phaser.Geom.Rectangle | null {
    const target = this.cells.find((c) => betsMatchForPreview(c.bet, bet));
    if (!target) return null;
    return target.rect.getBounds();
  }

  /** Return the Rectangle game objects covering the given bet, for drag-drop stroke application. */
  public getRectsForBet(bet: BetType): Phaser.GameObjects.Rectangle[] {
    return this.cells.filter((c) => betsMatchForPreview(c.bet, bet)).map((c) => c.rect);
  }

  /**
   * Wire gesture detection for the landscape board.
   *
   * Landscape orientation:
   *   - Horizontal drag: move between number COLUMNS (n ± 3)
   *   - Vertical drag:   move between number ROWS    (n ± 1)
   */
  public setOnBetPlace(cb: (bet: BetType, rects: Phaser.GameObjects.Rectangle[]) => void): void {
    let dragStart: {
      x: number;
      y: number;
      n: number;
      numCol: number; // 0-11: horizontal position (col-1)
      numRow: number; // 0-2:  vertical position (row)
      rect: Phaser.GameObjects.Rectangle;
    } | null = null;

    const findRect = (m: number): Phaser.GameObjects.Rectangle | null =>
      this.cells.find(
        (c) => c.bet.kind === 'straight' && (c.bet as { number: number }).number === m,
      )?.rect ?? null;

    this.cells.forEach(({ bet, rect }) => {
      rect.setInteractive();
      rect.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
        const isStraightNumber = bet.kind === 'straight' && (bet as { number: number }).number >= 1;
        if (!isStraightNumber) {
          cb(bet, [rect]);
          return;
        }
        const n = (bet as { number: number }).number;
        const numCol = Math.floor((n - 1) / 3); // 0-11
        // numRow: row 0=top (n%3==0), row 1=mid (n%3==2), row 2=bottom (n%3==1)
        const numRow = n % 3 === 0 ? 0 : n % 3 === 2 ? 1 : 2;
        dragStart = { x: ptr.x, y: ptr.y, n, numCol, numRow, rect };
      });
    });

    if (this.pointerupListener) {
      this.scene.input.off('pointerup', this.pointerupListener);
    }
    this.pointerupListener = (ptr: Phaser.Input.Pointer) => {
      if (!dragStart) return;
      const ds = dragStart;
      dragStart = null;

      const dx = ptr.x - ds.x;
      const dy = ptr.y - ds.y;
      const dist = Math.hypot(dx, dy);

      if (dist < 8) {
        cb({ kind: 'straight', number: ds.n as SlotNumber }, [ds.rect]);
        return;
      }

      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      const axisDominant =
        adx > ady ? adx / Math.max(ady, 0.001) > 2 : ady / Math.max(adx, 0.001) > 2;

      if (axisDominant) {
        let neighborN: number | null = null;
        if (adx >= ady) {
          // Horizontal drag → column step (n ± 3)
          if (dx > 0 && ds.numCol < 11) neighborN = ds.n + 3;
          else if (dx < 0 && ds.numCol > 0) neighborN = ds.n - 3;
        } else {
          // Vertical drag → row step (n ± 1)
          if (dy > 0 && ds.numRow < 2) neighborN = ds.n - 1;
          else if (dy < 0 && ds.numRow > 0) neighborN = ds.n + 1;
        }
        if (neighborN !== null) {
          const neighborRect = findRect(neighborN);
          if (neighborRect) {
            const [a, b] = [ds.n, neighborN].sort((x, y) => x - y);
            cb({ kind: 'split', numbers: [a, b] as [SlotNumber, SlotNumber] }, [
              ds.rect,
              neighborRect,
            ]);
            return;
          }
        }
        cb({ kind: 'straight', number: ds.n as SlotNumber }, [ds.rect]);
        return;
      }

      // Diagonal → corner bet (2×2 block)
      // Right+Down: n, n+3 (col+1), n-1 (row+1), n+2 (diag)
      // Right+Up:   n, n+3,         n+1 (row-1), n+4
      // Left+Down:  n, n-3 (col-1), n-1,         n-4
      // Left+Up:    n, n-3,         n+1,         n-2
      let cornerNs: number[] | null = null;
      if (dx > 0 && dy > 0 && ds.numCol < 11 && ds.numRow < 2)
        cornerNs = [ds.n, ds.n + 3, ds.n - 1, ds.n + 2];
      else if (dx > 0 && dy < 0 && ds.numCol < 11 && ds.numRow > 0)
        cornerNs = [ds.n, ds.n + 3, ds.n + 1, ds.n + 4];
      else if (dx < 0 && dy > 0 && ds.numCol > 0 && ds.numRow < 2)
        cornerNs = [ds.n, ds.n - 3, ds.n - 1, ds.n - 4];
      else if (dx < 0 && dy < 0 && ds.numCol > 0 && ds.numRow > 0)
        cornerNs = [ds.n, ds.n - 3, ds.n + 1, ds.n - 2];

      if (cornerNs !== null) {
        const cornerRects = cornerNs.map(findRect);
        if (cornerRects.every((r) => r !== null)) {
          const sorted = [...cornerNs].sort((a, b) => a - b) as [
            SlotNumber,
            SlotNumber,
            SlotNumber,
            SlotNumber,
          ];
          cb({ kind: 'corner', numbers: sorted }, cornerRects as Phaser.GameObjects.Rectangle[]);
          return;
        }
      }

      // Fallback: split on dominant axis, then straight
      let fallbackNeighbor: number | null = null;
      if (adx >= ady) {
        if (dx > 0 && ds.numCol < 11) fallbackNeighbor = ds.n + 3;
        else if (dx < 0 && ds.numCol > 0) fallbackNeighbor = ds.n - 3;
      } else {
        if (dy > 0 && ds.numRow < 2) fallbackNeighbor = ds.n - 1;
        else if (dy < 0 && ds.numRow > 0) fallbackNeighbor = ds.n + 1;
      }
      if (fallbackNeighbor !== null) {
        const fr = findRect(fallbackNeighbor);
        if (fr) {
          const [a, b] = [ds.n, fallbackNeighbor].sort((x, y) => x - y);
          cb({ kind: 'split', numbers: [a, b] as [SlotNumber, SlotNumber] }, [ds.rect, fr]);
          return;
        }
      }
      cb({ kind: 'straight', number: ds.n as SlotNumber }, [ds.rect]);
    };
    this.scene.input.on('pointerup', this.pointerupListener);
  }
}

function isRed(n: number): boolean {
  return RED_NUMBERS.has(n as SlotNumber);
}

function outsideSubParentLabel(kind: OutsideBetKind): string {
  switch (kind) {
    case 'red':
      return '♦';
    case 'black':
      return '♠';
    case 'even':
      return 'EVEN';
    case 'odd':
      return 'ODD';
    case 'low':
      return '1-18';
    case 'high':
      return '19-36';
  }
}

function betsMatchForPreview(a: BetType, b: BetType): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'straight':
      return a.number === (b as typeof a).number;
    case 'dozen':
    case 'column':
      return a.which === (b as typeof a).which;
    case 'split': {
      const sa = [...a.numbers].sort((x, y) => x - y);
      const sb = [...(b as typeof a).numbers].sort((x, y) => x - y);
      return sa[0] === sb[0] && sa[1] === sb[1];
    }
    case 'corner':
      return (
        [...a.numbers].sort((x, y) => x - y).join(',') ===
        [...(b as typeof a).numbers].sort((x, y) => x - y).join(',')
      );
    // Phase 21 (BOARD-21): outside-sub match by parentKind + sorted numbers
    case 'outside-sub': {
      const bb = b as typeof a;
      if (a.parentKind !== bb.parentKind) return false;
      if (a.numbers.length !== bb.numbers.length) return false;
      const sa = [...a.numbers].sort((x, y) => x - y);
      const sb = [...bb.numbers].sort((x, y) => x - y);
      return sa.every((v, i) => v === sb[i]);
    }
    case 'dozen-sub': {
      const bb = b as typeof a;
      return a.parentWhich === bb.parentWhich && a.subIdx === bb.subIdx;
    }
    default:
      return true;
  }
}

function buildOutsideSubGroups(cells: BoardCell[]): { cells: BoardCell[]; combined: boolean }[] {
  const subCells = cells.filter((c) => c.bet.kind === 'outside-sub');
  const byParent = new Map<OutsideBetKind, BoardCell[]>();
  for (const cell of subCells) {
    const kind = (cell.bet as Extract<BetType, { kind: 'outside-sub' }>).parentKind;
    if (!byParent.has(kind)) byParent.set(kind, []);
    byParent.get(kind)!.push(cell);
  }
  const groups: { cells: BoardCell[]; combined: boolean }[] = [];
  for (const siblings of byParent.values()) {
    if (siblings.length === 2) {
      const [a, b] = siblings as [BoardCell, BoardCell];
      if (a.row === b.row && Math.abs(a.col - b.col) === 1) {
        groups.push({ cells: [a, b].sort((x, y) => x.col - y.col), combined: true });
      } else {
        groups.push({ cells: [a], combined: false });
        groups.push({ cells: [b], combined: false });
      }
    } else {
      for (const c of siblings) groups.push({ cells: [c], combined: false });
    }
  }
  return groups;
}

function buildDozenSubClusters(
  cells: BoardCell[],
): { parentWhich: 1 | 2 | 3; cells: BoardCell[] }[] {
  const subCells = cells.filter((c) => c.bet.kind === 'dozen-sub');
  const byParent = new Map<number, BoardCell[]>();
  for (const cell of subCells) {
    const which = (cell.bet as Extract<BetType, { kind: 'dozen-sub' }>).parentWhich;
    if (!byParent.has(which)) byParent.set(which, []);
    byParent.get(which)!.push(cell);
  }
  const clusters: { parentWhich: 1 | 2 | 3; cells: BoardCell[] }[] = [];
  for (const [which, siblings] of byParent) {
    const byRow = new Map<number, BoardCell[]>();
    for (const cell of siblings) {
      if (!byRow.has(cell.row)) byRow.set(cell.row, []);
      byRow.get(cell.row)!.push(cell);
    }
    for (const rowCells of byRow.values()) {
      const sorted = [...rowCells].sort((a, b) => a.col - b.col);
      let run: BoardCell[] = [sorted[0]!];
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i]!.col === sorted[i - 1]!.col + 1) {
          run.push(sorted[i]!);
        } else {
          clusters.push({ parentWhich: which as 1 | 2 | 3, cells: run });
          run = [sorted[i]!];
        }
      }
      clusters.push({ parentWhich: which as 1 | 2 | 3, cells: run });
    }
  }
  return clusters;
}
